import "./LabelingRulesTable.scss";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import * as R from "ramda";
import cn from "classnames";
import { MdContentCopy, MdMode, MdDelete, MdReorder, MdInfoOutline } from "react-icons/md";
import { SegmentationMappingLabelRule } from "@blisspointmedia/bpm-types/dist/SegmentationMapping";
import {
  Button,
  ButtonType,
  ButtonFrameworkVariant,
  CellRenderer,
  CornerLocation,
  FrozenColumnsTable,
  OverlayTrigger,
  ToggleSwitch,
  Tooltip,
} from "../Components";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { CustomSegmentsData } from "./SegmentationMapping";
import { SystemGeneratedSegmentNames } from "./CustomSegments";
import { Warning500 } from "../utils/colors";

interface LabelingRulesTableProps {
  rules: any[];
  customSegments: CustomSegmentsData[] | undefined;
  activeTab: string;
  dataGranularity?: "ad" | "ad_group" | "campaign";
  onRuleDelete?: (rule: SegmentationMappingLabelRule) => void;
  onLiveToggle?: (rule: SegmentationMappingLabelRule) => void;
  setModalShow?: (show: boolean) => void;
  setRuleToEdit?: (rule: SegmentationMappingLabelRule | null) => void;
  setRuleToCopy?: (rule: SegmentationMappingLabelRule | null) => void;
  onRuleReorder?: (rules: SegmentationMappingLabelRule[]) => void;
}

interface LabelingRulesHeader {
  label: string | JSX.Element;
  field: string;
  flex: number;
  renderer: (data: any) => JSX.Element | string;
  name?: string;
}

const LabelingRulesTable: React.FC<LabelingRulesTableProps> = ({
  rules,
  customSegments,
  activeTab,
  dataGranularity = "campaign",
  onRuleDelete = () => {},
  onLiveToggle = () => {},
  setModalShow = () => {},
  setRuleToEdit = () => {},
  setRuleToCopy = () => {},
  onRuleReorder = () => {},
}) => {
  const [innerWidth, setInnerHeight] = useState<number>(window.innerWidth);
  useEffect(() => {
    window.addEventListener("resize", () => setInnerHeight(window.innerWidth));

    return () => {
      window.removeEventListener("resize", () => setInnerHeight(window.innerWidth));
    };
  }, [innerWidth]);
  const leftWidth = useMemo(() => (1 / 24) * innerWidth, [innerWidth]);

  const simpleCustomSegments = useMemo(() => {
    return customSegments?.filter(segment => {
      const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
      return !systemNames.includes(segment.segmentName);
    });
  }, [customSegments]);

  const findSegmentName = useCallback(
    (segmentId: number): string => {
      return (
        customSegments?.find(segment => Number(segment.segmentId) === segmentId)?.segmentName || ""
      );
    },
    [customSegments]
  );

  const findSegmentValue = useCallback(
    (valueId: number): string => {
      return (
        simpleCustomSegments
          ?.flatMap(segment => segment.values)
          .find(value => Number(value.valueId) === valueId)?.valueName || ""
      );
    },
    [simpleCustomSegments]
  );

  const handleRuleEditClick = useCallback(
    (rule: SegmentationMappingLabelRule) => {
      setModalShow(true);
      setRuleToEdit(rule);
    },
    [setModalShow, setRuleToEdit]
  );

  const handleRuleCopyClick = useCallback(
    (rule: SegmentationMappingLabelRule) => {
      setRuleToCopy(({
        ...rule,
        id: undefined,
        name: "",
      } as unknown) as SegmentationMappingLabelRule);
    },
    [setRuleToCopy]
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [ruleToDelete, setRuleToDelete] = useState<SegmentationMappingLabelRule | null>(null);
  const handleRuleDeleteClick = useCallback((rule: SegmentationMappingLabelRule) => {
    setShowDeleteDialog(true);
    setRuleToDelete(rule);
  }, []);

  const rightTableHeaders: LabelingRulesHeader[] = useMemo(() => {
    return [
      {
        label: "Rule ID",
        field: "name",
        flex: 0.5,
        renderer: data => {
          return (
            <div className={cn("ruleIdCell", { active: activeTab !== "all" })}>
              {activeTab !== "all" && <MdReorder />}
              {data.name || "-"}
            </div>
          );
        },
      },
      {
        label: "Segment",
        field: "setSegmentId",
        flex: 1,
        renderer: data => findSegmentName(data.setSegmentId),
      },
      {
        label: "Segment Value",
        field: "setSegmentValueId",
        flex: 1,
        renderer: data => findSegmentValue(data.setSegmentValueId),
      },
      {
        label: "If statement",
        field: "filterTokens",
        flex: 1.5,
        renderer: data => {
          const { filterTokens } = data;
          let statement = "";
          if (R.isNil(data.isAdvanced)) {
            return "";
          } else if (data.isAdvanced) {
            statement = filterTokens.map(filter => filter.label).join(" ");
          } else {
            statement = `* is like ${filterTokens.map(filter => filter.label).join(" or ")}`;
          }
          return <div style={{ justifyContent: "flex-start" }}>{statement}</div>;
        },
      },
      {
        label: "Created",
        field: "created",
        flex: 0.75,
        renderer: data => {
          const date = new Date(data.updatedAt).toLocaleDateString();
          const newDate = date === "Invalid Date" ? new Date().toLocaleDateString() : date;
          return (
            <span style={{ fontSize: "10px" }}>
              {data.createdBy} {newDate}
            </span>
          );
        },
      },
      {
        label: "Modified",
        field: "modified",
        flex: 0.75,
        renderer: data => {
          const date = new Date(data.updatedAt).toLocaleDateString();
          const newDate = date === "Invalid Date" ? new Date().toLocaleDateString() : date;
          return (
            <span style={{ fontSize: "10px" }}>
              {data.lastUpdatedBy} {newDate}
            </span>
          );
        },
      },
      {
        label: (
          <OverlayTrigger
            placement={OverlayTrigger.PLACEMENTS.TOP.RIGHT}
            overlay={
              <Tooltip>
                Enable or disable this rule. Only enabled rules can be applied on the labeling page.
              </Tooltip>
            }
          >
            <span className="enabledLabel">
              Enabled
              <MdInfoOutline color="#6B2DEF" />
            </span>
          </OverlayTrigger>
        ),
        field: "isActive",
        flex: 0.1,
        renderer: data => {
          return (
            <div>
              <ToggleSwitch
                checked={data.isActive}
                onChange={() => onLiveToggle(data)}
                label={data.isActive ? "Yes" : "No"}
                labelPosition="none"
              />
            </div>
          );
        },
      },
      {
        label: "",
        field: "actions",
        flex: 1.25,
        renderer: data => {
          return (
            <div className="actions">
              <Button
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.ICON_ONLY}
                onClick={() => {
                  handleRuleCopyClick(data);
                }}
                title="Copy Rule"
                icon={<MdContentCopy />}
              />
              <Button
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.ICON_ONLY}
                onClick={() => handleRuleEditClick(data)}
                title="Edit Rule"
                icon={<MdMode />}
              />
              <Button
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.ICON_ONLY}
                onClick={() => handleRuleDeleteClick(data)}
                title="Delete Rule"
                icon={<MdDelete />}
              />
            </div>
          );
        },
      },
    ];
  }, [
    activeTab,
    findSegmentName,
    findSegmentValue,
    handleRuleCopyClick,
    handleRuleDeleteClick,
    handleRuleEditClick,
    onLiveToggle,
  ]);

  const rightHeaderNames = useMemo(() => {
    return (rightTableHeaders || []).map(header => header.name || "");
  }, [rightTableHeaders]);

  const customLeftRenderer: CellRenderer<any> = useMemo(
    () => ({ data, style = {}, classes = [] }) => {
      return (
        <div
          className={`leftCellDiv ${classes.join(" ")}`}
          style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {activeTab === "all" ? "" : data.rulePriority}
        </div>
      );
    },
    [activeTab]
  );

  const [rightData, setRightData] = useState<any[][]>([]);

  const customCornerRenderer = useMemo(
    () => (corner: CornerLocation) => {
      if (corner === "nw") {
        return (
          <div
            className="stickyTableCell"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <div className={cn("leftCellHeader", { inactive: activeTab === "all" })}>
              Rule Priority
            </div>
          </div>
        );
      } else {
        return <div />;
      }
    },
    [activeTab]
  );

  const leftDataFields = ["rulePriority"];

  const noRowsRenderer = useCallback(() => {
    return rules.length === 0 ? <div className="noRows">No rules created yet.</div> : <div />;
  }, [rules.length]);

  const [localRules, setLocalRules] = useState<SegmentationMappingLabelRule[]>(rules);

  useEffect(() => {
    setLocalRules(rules);
  }, [rules]);

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const updated = [...localRules];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, moved);

      const reordered = updated.map((rule, idx) => ({
        ...rule,
        rulePriority: idx + 1,
      }));

      onRuleReorder(reordered);
      setLocalRules(reordered);
    },
    [localRules, onRuleReorder]
  );

  return (
    <div className="labelingRulesStickyTable">
      <AlertDialog
        show={showDeleteDialog}
        variant={AlertDialogVariants.WARNING}
        icon={<MdDelete color={Warning500} size={75} />}
        title="Delete rule?"
        body={`Any ${dataGranularity.replace(
          "_",
          " "
        )}s that are already labeled with this rule will remain labeled.`}
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onHide={() => {
          onRuleDelete(ruleToDelete as SegmentationMappingLabelRule);
          setShowDeleteDialog(false);
        }}
        secondaryAction={() => setShowDeleteDialog(false)}
      />
      <FrozenColumnsTable
        leftWidth={leftWidth}
        leftRenderer={customLeftRenderer}
        cornerRenderer={customCornerRenderer}
        rightTableHeaders={rightTableHeaders as any}
        rightTableHeaderNames={rightHeaderNames}
        originalRightData={localRules}
        leftDataFields={leftDataFields}
        rightData={rightData}
        setRightData={setRightData}
        noRowsRenderer={noRowsRenderer}
        moveRow={activeTab !== "all" ? moveRow : undefined}
        isDraggable={activeTab !== "all"}
      />
    </div>
  );
};

export default LabelingRulesTable;
